import {
  CalendarEvent,
  CalendarEventSerialized,
  InterviewSlot,
  InterviewSlotSerialized,
  SlotType
} from 'types/interviewSlots';

// Transform Date to serialized slot
export const serializeSlot = (
  slot: InterviewSlot
): InterviewSlotSerialized => ({
  id: slot.id,
  start: slot.start.toISOString(),
  end: slot.end.toISOString()
});

// Transform serialized slot to Date objects
export const deserializeSlot = (slot: InterviewSlotSerialized) => ({
  id: slot.id,
  start: new Date(slot.start),
  end: new Date(slot.end)
});

// Transform array of slots to Date objects
export const deserializeSlots = (slots: InterviewSlotSerialized[]) =>
  slots.map(deserializeSlot);

// Parse time zone like "UTC+3" or "UTC-5" to minutes
export const parseTimeZoneOffset = (timeZone: string): number => {
  const match = timeZone.match(/UTC([+-])(\d+)(?::(\d+))?/);
  if (!match) {
    console.warn(`Invalid timezone format: ${timeZone}, defaulting to UTC`);
    return 0;
  }

  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = match[3] ? parseInt(match[3], 10) : 0;

  return sign * (hours * 60 + minutes);
};

// Get current user's time zone offset in minutes
export const getCurrentTimeZoneOffset = (): number => {
  // getTimezoneOffset() returns offset in minutes FROM UTC
  // But with opposite sign (positive value for western time zones)
  return -new Date().getTimezoneOffset();
};

// Convert slot from one time zone to another
export const convertSlotTimeZone = (
  slot: InterviewSlotSerialized,
  fromTimeZone: string,
  toTimeZoneOffset?: number
): InterviewSlotSerialized => {
  const fromOffset = parseTimeZoneOffset(fromTimeZone);
  const toOffset = toTimeZoneOffset ?? getCurrentTimeZoneOffset();
  const offsetDiff = toOffset - fromOffset; // разница в минутах

  const startDate = new Date(slot.start);
  const endDate = new Date(slot.end);

  // Применяем разницу во времени
  const convertedStart = new Date(startDate.getTime() + offsetDiff * 60 * 1000);
  const convertedEnd = new Date(endDate.getTime() + offsetDiff * 60 * 1000);

  return {
    ...slot,
    start: convertedStart.toISOString(),
    end: convertedEnd.toISOString()
  };
};

// Convert array of slots from one time zone to another
export const convertSlotsTimeZone = (
  slots: InterviewSlotSerialized[],
  fromTimeZone: string,
  toTimeZoneOffset?: number
): InterviewSlotSerialized[] => {
  return slots.map((slot) =>
    convertSlotTimeZone(slot, fromTimeZone, toTimeZoneOffset)
  );
};

// Create combined events for calendar
export const createCombinedEvents = (
  selectedSlots: InterviewSlotSerialized[],
  proposedSlots: InterviewSlotSerialized[],
  selectedSlot: InterviewSlotSerialized | null
): CalendarEvent[] => {
  const selectedEvents: CalendarEvent[] = selectedSlots.map((slot) => ({
    ...deserializeSlot(slot),
    type: SlotType.Selected
  }));

  const proposedEvents: CalendarEvent[] = proposedSlots.map((slot) => ({
    ...deserializeSlot(slot),
    type: slot.id === selectedSlot?.id ? SlotType.Selected : SlotType.Proposed
  }));

  return [...selectedEvents, ...proposedEvents];
};
